import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GrantWriteResult, PatchRoleBody, PutGrantBody, RoleCard, RoleGrants, RoleMembers, RolesList } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { roleApi } from "../../api/client.js";
import { invalidateDashboard } from "../dashboard/dashboard-cache.js";

/** A typed "you don't administer any role" — the workspace hides the entry, the view shows the no-access state. */
export class RolesForbiddenError extends Error {
  readonly code = "ROLES_FORBIDDEN" as const;
  constructor() {
    super("you do not administer any role");
  }
}

/** `GET /roles` (ADR-030 Phase A). A 403 is the entry gate, not a failure — it does not retry. */
export function useRoles(session: AuthSession | null) {
  return useQuery<RolesList, Error>({
    queryKey: ["roles", session?.realm, session?.sub],
    enabled: session !== null,
    retry: (count, err) => !(err instanceof RolesForbiddenError) && count < 1,
    queryFn: async () => {
      const res = await roleApi.listRoles();
      if (res.status === 403) throw new RolesForbiddenError();
      if (res.status !== 200) throw new Error(`GET /roles returned ${res.status}`);
      return res.body;
    },
  });
}

/** `GET /roles/:id/grants` — fetched once a role is selected. */
export function useRoleGrants(session: AuthSession | null, roleId: number | null) {
  return useQuery<RoleGrants, Error>({
    queryKey: ["role-grants", session?.realm, session?.sub, roleId],
    enabled: session !== null && roleId !== null,
    queryFn: async () => {
      const res = await roleApi.getRoleGrants({ params: { id: roleId! } });
      if (res.status !== 200) throw new Error(`GET /roles/${String(roleId)}/grants returned ${res.status}`);
      return res.body;
    },
  });
}

/** `GET /roles/:id/members` (ADR-030 Phase D) — the read-only roster, fetched once a role is selected. */
export function useRoleMembers(session: AuthSession | null, roleId: number | null) {
  return useQuery<RoleMembers, Error>({
    queryKey: ["role-members", session?.realm, session?.sub, roleId],
    enabled: session !== null && roleId !== null,
    queryFn: async () => {
      const res = await roleApi.getRoleMembers({ params: { id: roleId! } });
      if (res.status !== 200) throw new Error(`GET /roles/${String(roleId)}/members returned ${res.status}`);
      return res.body;
    },
  });
}

/** A typed mutation failure from `PATCH /roles/:id` — the host branches on `code` (a 409 ROLE_STALE reconciles). */
export class RoleWriteError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`PATCH /roles returned ${String(status)} ${code}`);
  }
}

/** `PATCH /roles/:id` (ADR-030 Phase B). Invalidates the roles list on success (the card's `updatedAt` moved). */
export function usePatchRole(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation<RoleCard, RoleWriteError, { id: number; body: PatchRoleBody }>({
    mutationFn: async ({ id, body }) => {
      const res = await roleApi.patchRole({ params: { id }, body });
      if (res.status !== 200) throw new RoleWriteError(res.status, (res.body as { code?: string }).code ?? "UNKNOWN");
      return res.body;
    },
    onSuccess: (card, { id }) => {
      // The fresh card (its new token) lands in the cache AT ONCE — a second edit in the refetch window must carry the
      // new `updatedAt`, not bounce on a spurious 409 (panel); then the list refetches for anything else that moved.
      qc.setQueryData<RolesList>(["roles", session?.realm, session?.sub], (old) => (old ? { ...old, roles: old.roles.map((r) => (r.id === card.id ? card : r)) } : old));
      void qc.invalidateQueries({ queryKey: ["roles", session?.realm, session?.sub] });
      // A level change moves every grant's EFFECTIVE level — the badges read that from the grants query (panel).
      void qc.invalidateQueries({ queryKey: ["role-grants", session?.realm, session?.sub, id] });
      invalidateDashboard(qc, session); // the Tasks page's role warnings + the Übersicht count (ADR-034 diff panel)
    },
  });
}

/** `PUT /roles/:id/grants/:facilityId` and `DELETE …` (ADR-030 Phase C). Invalidates the role's grants on success. */
/**
 * The grant writes (ADR-030 Phase C). The role id travels IN the mutation variables, never in the hook's closure: a
 * mutation paused offline stays `pending`, and TanStack swaps a pending mutation's options on every re-render — a
 * closed-over role id would let a role switched in the meantime re-point role A's queued write at role B (panel).
 */
export function useGrantWrites(session: AuthSession | null) {
  const qc = useQueryClient();
  // A grant write moves the role's grants AND its card's `grantCount` (the tree's "no facilities" marker, Phase D).
  const invalidate = (roleId: number) => {
    void qc.invalidateQueries({ queryKey: ["role-grants", session?.realm, session?.sub, roleId] });
    void qc.invalidateQueries({ queryKey: ["roles", session?.realm, session?.sub] });
    invalidateDashboard(qc, session); // a first grant clears the Tasks page's "no facilities" warning at once (ADR-034 diff panel)
  };
  const put = useMutation<GrantWriteResult, RoleWriteError, { roleId: number; facilityId: number; body: PutGrantBody }>({
    mutationFn: async ({ roleId, facilityId, body }) => {
      const res = await roleApi.putGrant({ params: { id: roleId, facilityId }, body });
      if (res.status !== 200) throw new RoleWriteError(res.status, (res.body as { code?: string }).code ?? "UNKNOWN");
      return res.body;
    },
    onSuccess: (_r, v) => invalidate(v.roleId),
  });
  const revoke = useMutation<GrantWriteResult, RoleWriteError, { roleId: number; facilityId: number; applyToChildren: boolean }>({
    mutationFn: async ({ roleId, facilityId, applyToChildren }) => {
      const res = await roleApi.deleteGrant({ params: { id: roleId, facilityId }, query: { applyToChildren: applyToChildren ? "true" : "false" }, body: {} });
      if (res.status !== 200) throw new RoleWriteError(res.status, (res.body as { code?: string }).code ?? "UNKNOWN");
      return res.body;
    },
    onSuccess: (_r, v) => invalidate(v.roleId),
  });
  return { put, revoke };
}
